import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import {
    Component,
    ElementRef,
    HostListener,
    Injector,
    ViewChild,
    afterNextRender,
    inject,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import Joi from 'joi';
import { lastValueFrom } from 'rxjs';
import {
    IFeedbackDTO,
    IFeedbackOption,
} from 'src/app/interfaces/feedback.interfaces';
import { HttpNewService } from 'src/app/services/application/http-new.service';
import { LoadService } from 'src/app/services/application/load.service';
import { PatientService } from 'src/app/services/patient.service';
import { AlertService } from 'src/lib/ariadna/alert';
import { Utils } from 'src/app/services/utils';

@Component({
    selector: 'app-feedback',
    templateUrl: './feedback.component.html',
    styleUrls: ['./feedback.component.scss'],
    standalone: false
})
export class FeedbackModalComponent {
    public form: FormGroup = new FormGroup({
        subject: new FormControl('', Validators.required),
        subsubject: new FormControl(''),
        message: new FormControl('', Validators.required),
        email: new FormControl('', [Validators.email, Validators.required]),
    });
    public subjectOptions: IFeedbackOption[] = [];
    public loading = false;
    protected patientEmail: string | null = null;
    private _injector = inject(Injector);
    public isSubjectOpen = false;
    public isSubsubjectOpen = false;
    public selectedSubject: any = null;
    public selectedSubsubject: string | null = null;

    // For confirmation dialog
    public showConfirmation = false;

    // Store initial form values to compare later
    private initialFormValues: any;

    @ViewChild('subjectDropdown') public subjectDropdown!: ElementRef;
    @ViewChild('subsubjectDropdown') public subsubjectDropdown!: ElementRef;
    @ViewChild('autosize') public autosize!: CdkTextareaAutosize;
    @ViewChild('focusInput') public focusInput!: ElementRef<HTMLInputElement>;

    public triggerResize() {
        // Wait for content to render, then trigger textarea resize.
        afterNextRender(
            () => {
                this.autosize.resizeToFitContent(true);
            },
            {
                injector: this._injector,
            },
        );
    }

    public constructor(
        private load: LoadService,
        private http: HttpNewService,
        private alertService: AlertService,
        private patientService: PatientService,
        private el: ElementRef,
        private dialogRef: MatDialogRef<FeedbackModalComponent>,
    ) {
        if (
            load.configData?.CLIENT_SETTINGS.OTHER.FEEDBACK_SUBJECTS?.length ===
            0
        ) {
            alertService.error('Нет полей для формы обратной связи!');
            throw new Error('Нет полей для формы обратной связи!');
        }

        // Disable default closing behavior
        this.dialogRef.disableClose = true;

        // Subscribe to backdrop clicks
        this.dialogRef.backdropClick().subscribe(() => {
            this.attemptClose();
        });
    }

    @HostListener('document:keydown.escape', ['$event'])
    public handleKeyboardEvent(event: KeyboardEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.attemptClose();
    }

    @HostListener('document:click', ['$event'])
    public onClickOutside(event: MouseEvent): void {
        const target = event.target as HTMLElement;

        // Only check if the subject dropdown exists
        if (
            this.subjectDropdown &&
            !this.subjectDropdown.nativeElement.contains(target)
        ) {
            this.isSubjectOpen = false;
        }

        // Only check if the subsubject dropdown exists
        if (
            this.subsubjectDropdown &&
            !this.subsubjectDropdown.nativeElement.contains(target)
        ) {
            this.isSubsubjectOpen = false;
        }
    }

    public async ngOnInit() {
        try {
            const rawJson =
                this.load.configData?.CLIENT_SETTINGS?.OTHER
                    .FEEDBACK_SUBJECTS ?? '';
            const parsed = JSON.parse(rawJson);

            // Normalize email: ensure it's always an array
            this.subjectOptions = parsed['subjects'].map((subj: any) => ({
                title: subj.title,
                email: Array.isArray(subj.email) ? subj.email : [subj.email],
                subthemes: Array.isArray(subj.subthemes) ? subj.subthemes : [],
            }));

            const patientInfo = await lastValueFrom(
                this.patientService.getServerPatientInfo$(),
            );
            this.patientEmail = patientInfo.email ?? null;

            this.form.patchValue({
                email: this.patientEmail || '',
                message: '',
            });

            this.initialFormValues = {
                email: this.patientEmail || '',
                message: '',
                subject: null,
                subsubject: '',
            };
        } catch (error) {
            console.error('Ошибка загрузки/парсинга данных формы:', error);
            this.alertService.error(
                'Не удалось загрузить форму обратной связи',
            );
        }

        // Email validator
        const emailControl = this.form.get('email');
        emailControl?.setValidators([
            Validators.required,
            this.joiEmailValidator.bind(this),
        ]);
        emailControl?.updateValueAndValidity();

        // Watch subject changes
        this.form
            .get('subject')
            ?.valueChanges.subscribe((subject: IFeedbackOption) => {
                this.selectedSubject = subject;
                const subControl = this.form.get('subsubject');

                if (subject?.subthemes && subject.subthemes.length > 0) {
                    subControl?.setValidators([Validators.required]);
                } else {
                    subControl?.clearValidators();
                }
                subControl?.updateValueAndValidity();
            });
    }
    public ngAfterViewInit() {
        afterNextRender(
            () => {
                this.focusInput?.nativeElement.focus();
            },
            {
                injector: this._injector,
            },
        );
    }

    public toggleSubjectDropdown() {
        this.isSubjectOpen = !this.isSubjectOpen;
        this.isSubsubjectOpen = false; // Close other dropdown
    }

    public toggleSubsubjectDropdown() {
        this.isSubsubjectOpen = !this.isSubsubjectOpen;
        this.isSubjectOpen = false; // Close other dropdown
    }

    public selectSubject(subject: any, event: Event) {
        event.stopPropagation(); // Prevent closure on selection
        this.form.get('subject')?.setValue(subject);
        this.selectedSubject = subject;

        // Reset subsubject selection when changing subject
        this.selectedSubsubject = null;
        this.form.get('subsubject')?.setValue(null);

        this.isSubjectOpen = false;
    }

    public selectSubsubject(subsubject: string, event: Event) {
        event.stopPropagation(); // Prevent closure on selection
        this.form.get('subsubject')?.setValue(subsubject);
        this.selectedSubsubject = subsubject;
        this.isSubsubjectOpen = false;
    }

    // Check if form has been modified
    private isFormModified(): boolean {
        if (
            (this.form.controls['email'].value &&
                this.form.controls['email'].dirty) ||
            this.form.controls['subject'].value.title ||
            this.form.controls['message'].value
        )
            return true;
        else return false;

        // const currentValues = this.form.value;
        // return (
        //     currentValues.message !== this.initialFormValues.message ||
        //     currentValues.email !== this.initialFormValues.email ||
        //     currentValues.subject !== this.initialFormValues.subject ||
        //     currentValues.subsubject !== this.initialFormValues.subsubject
        // );
    }

    // New methods for handling close confirmation
    public attemptClose(): void {
        // Check if form has any content
        if (this.isFormModified()) {
            this.showConfirmation = true;
        } else {
            this.dialogRef.close();
        }
    }

    public cancelClose(): void {
        this.showConfirmation = false;
    }

    public confirmClose(): void {
        this.dialogRef.close();
    }

    public async onSubmit() {
        this.loading = true;
        this.dialogRef.disableClose = false;
        if (!this.form.valid) {
            this.alertService.error('Заполните все обязательные поля!');
            this.loading = false;
            return;
        }

        const data: IFeedbackDTO = {
            subject: this.form.controls['subject'].value.title,
            mailFrom: this.form.controls['email'].value,
            mailTo: this.form.controls['subject'].value.email,
            content: this.form.controls['message'].value,
        };

        if (this.form.controls['subsubject'].value) {
            data['subSubject'] = this.form.controls['subsubject'].value;
        }
        try {
            await lastValueFrom(
                this.http.post<void>(
                    '/feedback/send',
                    data,
                    false,
                ),
            );
            this.alertService.success('Ваше обращение успешно отправлено!');
            this.dialogRef.close();
        } catch (error: unknown) {
            this.alertService.error(
                Utils.getErrorMessage(error, 'Не удалось отправить обращение!'),
            );
            this.dialogRef.disableClose = false; // Теперь можно закрыть
            console.error('Ошибка отправки:', Utils.getErrorMessage(error));
        } finally {
            this.loading = false;
        }
    }

    private emailSchema = Joi.string().email({ tlds: { allow: false } });

    public joiEmailValidator(control: FormControl): { [key: string]: any } | null {
        const value = control.value?.trim();
        if (!value) return null;
        const isValid = this.emailSchema.validate(value).error === undefined;
        return isValid ? null : { invalidEmail: true };
    }
}
